import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  onSnapshot,
  serverTimestamp,
  writeBatch,
  getCountFromServer,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  PpcFarmKeywordDocumentClient,
  PpcFarmKeywordStats,
  KeywordValidationResult,
  BulkKeywordInput,
  PpcFarmKeywordFilters,
  PpcFarmKeywordSortOptions,
  PPC_FARM_KEYWORD_COLLECTIONS
} from '../types/ppcFarmTypes';

/**
 * Service for managing keyword discovery pipeline
 * Handles CRUD operations for keywords and provides validation
 */
class KeywordDiscoveryService {
  private readonly keywordsCollection = PPC_FARM_KEYWORD_COLLECTIONS.KEYWORDS;
  private readonly sellerKeywordMapCollection = PPC_FARM_KEYWORD_COLLECTIONS.SELLER_KEYWORD_MAP;

  /**
   * Get keyword statistics for dashboard display
   */
  async getKeywordStats(): Promise<PpcFarmKeywordStats> {
    try {
      const keywordsRef = collection(db, this.keywordsCollection);
      
      // Get counts for each status
      const [totalQuery, pendingQuery, inProgressQuery, completedQuery, errorQuery] = await Promise.all([
        getCountFromServer(query(keywordsRef)),
        getCountFromServer(query(keywordsRef, where('status', '==', 'pending'))),
        getCountFromServer(query(keywordsRef, where('status', '==', 'in_progress'))),
        getCountFromServer(query(keywordsRef, where('status', '==', 'completed'))),
        getCountFromServer(query(keywordsRef, where('status', '==', 'error')))
      ]);

      // Get completed keywords to calculate total sellers found
      const completedKeywords = await getDocs(
        query(keywordsRef, where('status', '==', 'completed'))
      );

      let totalSellersFound = 0;
      let totalAsinsDiscovered = 0;

      completedKeywords.docs.forEach(doc => {
        const data = doc.data() as PpcFarmKeywordDocumentClient;
        if (data.stats) {
          totalSellersFound += data.stats.sellers_found || 0;
          totalAsinsDiscovered += data.stats.asins_discovered || 0;
        }
      });

      return {
        total_keywords: totalQuery.data().count,
        pending_keywords: pendingQuery.data().count,
        in_progress_keywords: inProgressQuery.data().count,
        completed_keywords: completedQuery.data().count,
        error_keywords: errorQuery.data().count,
        total_sellers_found: totalSellersFound,
        total_asins_discovered: totalAsinsDiscovered
      };
    } catch (error) {
      console.error('Error getting keyword stats:', error);
      throw new Error('Failed to fetch keyword statistics');
    }
  }

  /**
   * Get keywords with filtering, sorting, and pagination
   */
  async getKeywords(
    filters: Partial<PpcFarmKeywordFilters> = {},
    sortOptions: PpcFarmKeywordSortOptions = { field: 'created_at', direction: 'desc' },
    pageSize: number = 50,
    lastDoc?: any
  ): Promise<{ keywords: PpcFarmKeywordDocumentClient[]; hasMore: boolean }> {
    try {
      const keywordsRef = collection(db, this.keywordsCollection);
      let q = query(keywordsRef);

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.created_by) {
        q = query(q, where('created_by', '==', filters.created_by));
      }

      // Apply sorting
      q = query(q, orderBy(sortOptions.field, sortOptions.direction));

      // Apply pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      q = query(q, limit(pageSize + 1)); // Get one extra to check if there are more

      const snapshot = await getDocs(q);
      const keywords: PpcFarmKeywordDocumentClient[] = [];
      
      snapshot.docs.forEach((doc, index) => {
        if (index < pageSize) { // Only include up to pageSize items
          const data = doc.data();
          keywords.push({
            ...data,
            created_at: data.created_at as Timestamp,
            last_processed: data.last_processed as Timestamp | null,
            stats: data.stats ? {
              ...data.stats,
              processed_at: data.stats.processed_at as Timestamp | null
            } : undefined
          } as PpcFarmKeywordDocumentClient);
        }
      });

      // Apply search filter on client side (for simplicity)
      let filteredKeywords = keywords;
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredKeywords = keywords.filter(keyword => 
          keyword.term.toLowerCase().includes(searchTerm)
        );
      }

      return {
        keywords: filteredKeywords,
        hasMore: snapshot.docs.length > pageSize
      };
    } catch (error) {
      console.error('Error getting keywords:', error);
      throw new Error('Failed to fetch keywords');
    }
  }

  /**
   * Validate keywords for duplicates and format issues
   */
  async validateKeywords(keywords: string[]): Promise<KeywordValidationResult[]> {
    try {
      const results: KeywordValidationResult[] = [];
      
      // Get existing keywords for duplicate check
      const existingKeywords = await this.getExistingKeywordTerms();
      const existingSet = new Set(existingKeywords.map(k => k.toLowerCase()));

      // Track keywords within this input session to detect internal duplicates
      const currentInputSet = new Set<string>();

      for (const keyword of keywords) {
        const trimmed = keyword.trim();
        const result: KeywordValidationResult = {
          keyword: trimmed,
          is_valid: true,
          is_duplicate: false
        };

        // Check if empty
        if (!trimmed) {
          result.is_valid = false;
          result.error_message = 'Keyword cannot be empty';
        }
        // Check length (2-100 characters)
        else if (trimmed.length < 2 || trimmed.length > 100) {
          result.is_valid = false;
          result.error_message = 'Keyword must be between 2 and 100 characters';
        }
        // Check for invalid characters (allow letters, numbers, spaces, hyphens, apostrophes)
        else if (!/^[a-zA-Z0-9\s\-']+$/.test(trimmed)) {
          result.is_valid = false;
          result.error_message = 'Keyword contains invalid characters';
        }
        // Check for duplicate in existing database
        else if (existingSet.has(trimmed.toLowerCase())) {
          result.is_duplicate = true;
          result.error_message = 'Keyword already exists in database';
        }
        // Check for duplicate within current input
        else if (currentInputSet.has(trimmed.toLowerCase())) {
          result.is_duplicate = true;
          result.error_message = 'Duplicate keyword in current input';
        }
        else {
          // Add to current input set if it's valid and not a duplicate
          currentInputSet.add(trimmed.toLowerCase());
        }

        results.push(result);
      }

      return results;
    } catch (error) {
      console.error('Error validating keywords:', error);
      throw new Error('Failed to validate keywords');
    }
  }

  /**
   * Add keywords in bulk with validation
   */
  async addKeywords(input: BulkKeywordInput): Promise<{ 
    success: boolean; 
    added: number; 
    skipped: number; 
    errors: string[] 
  }> {
    try {
      // Validate keywords first
      const validationResults = await this.validateKeywords(input.keywords);
      
      // Filter out invalid and duplicate keywords
      const validKeywords = validationResults.filter(r => r.is_valid && !r.is_duplicate);
      const errors: string[] = [];
      
      validationResults.forEach(result => {
        if (!result.is_valid || result.is_duplicate) {
          errors.push(`"${result.keyword}": ${result.error_message}`);
        }
      });

      if (validKeywords.length === 0) {
        return {
          success: false,
          added: 0,
          skipped: input.keywords.length,
          errors: ['No valid keywords to add']
        };
      }

      // Get next priority number
      const nextPriority = await this.getNextPriority();

      // Create batch write
      const batch = writeBatch(db);
      const keywordsRef = collection(db, this.keywordsCollection);

      validKeywords.forEach((result, index) => {
        const docRef = doc(keywordsRef);
        batch.set(docRef, {
          term: result.keyword,
          status: 'pending',
          last_processed: null,
          created_at: serverTimestamp(),
          priority: nextPriority + index,
          retry_count: 0,
          metadata: {
            source: input.source,
            notes: input.notes || null
          }
        });
      });

      await batch.commit();

      return {
        success: true,
        added: validKeywords.length,
        skipped: input.keywords.length - validKeywords.length,
        errors
      };
    } catch (error) {
      console.error('Error adding keywords:', error);
      throw new Error('Failed to add keywords');
    }
  }

  /**
   * Delete a keyword
   */
  async deleteKeyword(keywordId: string): Promise<void> {
    try {
      const keywordRef = doc(db, this.keywordsCollection, keywordId);
      await deleteDoc(keywordRef);
    } catch (error) {
      console.error('Error deleting keyword:', error);
      throw new Error('Failed to delete keyword');
    }
  }

  /**
   * Update keyword status (used by the serverless function)
   */
  async updateKeywordStatus(
    keywordId: string, 
    status: 'pending' | 'in_progress' | 'completed' | 'error',
    errorMessage?: string,
    stats?: { sellers_found: number; asins_discovered: number }
  ): Promise<void> {
    try {
      const keywordRef = doc(db, this.keywordsCollection, keywordId);
      const updateData: any = {
        status,
        last_processed: serverTimestamp()
      };

      if (errorMessage) {
        updateData.error_message = errorMessage;
        updateData.retry_count = updateData.retry_count ? updateData.retry_count + 1 : 1;
      }

      if (stats) {
        updateData.stats = {
          sellers_found: stats.sellers_found,
          asins_discovered: stats.asins_discovered,
          processed_at: serverTimestamp()
        };
      }

      await updateDoc(keywordRef, updateData);
    } catch (error) {
      console.error('Error updating keyword status:', error);
      throw new Error('Failed to update keyword status');
    }
  }

  /**
   * Set up real-time listener for keyword updates
   */
  subscribeToKeywords(
    callback: (keywords: PpcFarmKeywordDocumentClient[]) => void,
    filters: Partial<PpcFarmKeywordFilters> = {}
  ): () => void {
    const keywordsRef = collection(db, this.keywordsCollection);
    let q = query(keywordsRef, orderBy('created_at', 'desc'), limit(100));

    // Apply filters
    if (filters.status && filters.status !== 'all') {
      q = query(keywordsRef, where('status', '==', filters.status), orderBy('created_at', 'desc'), limit(100));
    }

    return onSnapshot(q, (snapshot) => {
      const keywords: PpcFarmKeywordDocumentClient[] = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        keywords.push({
          ...data,
          created_at: data.created_at as Timestamp,
          last_processed: data.last_processed as Timestamp | null,
          stats: data.stats ? {
            ...data.stats,
            processed_at: data.stats.processed_at as Timestamp | null
          } : undefined
        } as PpcFarmKeywordDocumentClient);
      });

      // Apply search filter on client side
      let filteredKeywords = keywords;
      if (filters.search && filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase().trim();
        filteredKeywords = keywords.filter(keyword =>
          keyword.term.toLowerCase().includes(searchTerm)
        );
      }

      callback(filteredKeywords);
    }, (error) => {
      console.error('Error in keyword subscription:', error);
    });
  }

  /**
   * Get existing keyword terms for duplicate checking
   */
  private async getExistingKeywordTerms(): Promise<string[]> {
    try {
      const keywordsRef = collection(db, this.keywordsCollection);
      const snapshot = await getDocs(keywordsRef);
      
      return snapshot.docs.map(doc => doc.data().term as string);
    } catch (error) {
      console.error('Error getting existing keywords:', error);
      return [];
    }
  }

  /**
   * Get the next priority number for new keywords
   */
  private async getNextPriority(): Promise<number> {
    try {
      const keywordsRef = collection(db, this.keywordsCollection);
      const q = query(keywordsRef, orderBy('priority', 'desc'), limit(1));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 1;
      }
      
      const highestPriority = snapshot.docs[0].data().priority as number;
      return highestPriority + 1;
    } catch (error) {
      console.error('Error getting next priority:', error);
      return 1;
    }
  }
}

export const keywordDiscoveryService = new KeywordDiscoveryService();